import React, { useState, useEffect, useCallback } from "react";
import { collaborationApi } from "../../../services/api/collaborationApi";
import { researchRoomApi } from "../../../services/api/researchRoomApi";
import { toast } from "react-toastify";
import { FaPlus, FaTimes } from "react-icons/fa";
import "./PostForms.css";
import "./CollaborationPostForm.css";

const CollaborationPostForm = ({ onSuccess, onCancel }) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [content, setContent] = useState("");
  const [positions, setPositions] = useState([{ role: "", count: 1 }]);
  const [requiredSkills, setRequiredSkills] = useState("");
  const [duration, setDuration] = useState("long-term");
  const [collaborationType, setCollaborationType] = useState("research");
  const [researchRoom, setResearchRoom] = useState("");
  const [deadline, setDeadline] = useState("");
  const [myRooms, setMyRooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingRooms, setLoadingRooms] = useState(false);

  // Load user's research rooms where they are leader
  useEffect(() => {
    loadMyRooms();
  }, []);

  const loadMyRooms = async () => {
    setLoadingRooms(true);
    try {
      const result = await researchRoomApi.getMyRooms("leader");
      if (result.data.success) {
        setMyRooms(result.data.data || []);
      }
    } catch (error) {
      // Not critical, so we don't show error toast
    } finally {
      setLoadingRooms(false);
    }
  };

  // Handle position changes
  const handlePositionChange = (index, field, value) => {
    const updatedPositions = [...positions];
    updatedPositions[index][field] =
      field === "count" ? parseInt(value) || 1 : value;
    setPositions(updatedPositions);
  };

  const addPosition = () => {
    if (positions.length < 10) {
      setPositions([...positions, { role: "", count: 1 }]);
    } else {
      toast.error("Maximum 10 positions allowed");
    }
  };

  const removePosition = (index) => {
    if (positions.length > 1) {
      setPositions(positions.filter((_, i) => i !== index));
    } else {
      toast.error("At least one position is required");
    }
  };

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      // Validation
      if (!title.trim()) {
        toast.error("Please enter a collaboration title");
        return;
      }

      if (title.trim().length > 200) {
        toast.error("Title must be at most 200 characters");
        return;
      }

      if (!description.trim()) {
        toast.error("Please enter a description");
        return;
      }

      if (description.trim().length > 5000) {
        toast.error("Description must be at most 5000 characters");
        return;
      }

      // Validate positions
      const validPositions = positions.filter(
        (p) => p.role.trim() && p.count > 0
      );

      if (validPositions.length === 0) {
        toast.error("Please add at least one valid position");
        return;
      }

      // Parse skills
      const skillsArray = requiredSkills
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

      setLoading(true);
      try {
        const result = await collaborationApi.createCollaboration({
          postType: "collaboration",
          content: content.trim(),
          collaboration: {
            title: title.trim(),
            description: description.trim(),
            requiredSkills: skillsArray,
            duration,
            collaborationType,
            researchRoom: researchRoom || null,
            positions: validPositions.map((p) => ({
              role: p.role.trim(),
              count: parseInt(p.count),
            })),
            deadline: deadline || null,
          },
          visibility: "public",
        });

        if (result.data.success) {
          toast.success("Collaboration post created successfully!");
          if (onSuccess) {
            onSuccess(result.data.post);
          }

          // Reset form
          setTitle("");
          setDescription("");
          setContent("");
          setPositions([{ role: "", count: 1 }]);
          setRequiredSkills("");
          setDuration("long-term");
          setCollaborationType("research");
          setResearchRoom("");
          setDeadline("");
        }
      } catch (error) {
        toast.error(
          error.response?.data?.message || "Failed to create collaboration"
        );
      } finally {
        setLoading(false);
      }
    },
    [
      title,
      description,
      content,
      positions,
      requiredSkills,
      duration,
      collaborationType,
      researchRoom,
      deadline,
      onSuccess,
    ]
  );

  return (
    <form onSubmit={handleSubmit} className="collaboration-post-form post-form">

      {/* Collaboration Title */}
      <div className="form-group">
        <label htmlFor="collab-title">Collaboration Title *</label>
        <input
          id="collab-title"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g., Looking for ML Engineers for Research Project"
          maxLength={200}
          required
        />
        <small className="char-count">{title.length}/200 characters</small>
      </div>

      {/* Description */}
      <div className="form-group">
        <label htmlFor="collab-description">Project Description *</label>
        <textarea
          id="collab-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Describe the project, goals, expectations, and requirements..."
          rows={6}
          maxLength={5000}
          required
        />
        <small className="char-count">
          {description.length}/5000 characters
        </small>
      </div>

      {/* Additional Content */}
      <div className="form-group">
        <label htmlFor="collab-content">
          Additional Information (Optional)
        </label>
        <textarea
          id="collab-content"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Any other details you'd like to share..."
          rows={4}
          maxLength={2000}
        />
      </div>

      {/* Positions */}
      <div className="form-group positions-group">
        <label>Positions Needed *</label>
        <div className="positions-list">
          {positions.map((position, index) => (
            <div key={index} className="position-row">
              <input
                type="text"
                value={position.role}
                onChange={(e) =>
                  handlePositionChange(index, "role", e.target.value)
                }
                placeholder="Role (e.g., Data Scientist)"
                required
              />
              <input
                type="number"
                value={position.count}
                onChange={(e) =>
                  handlePositionChange(index, "count", e.target.value)
                }
                min="1"
                max="20"
                className="count-input"
                required
              />
              {positions.length > 1 && (
                <button
                  type="button"
                  className="remove-position-btn"
                  onClick={() => removePosition(index)}
                  title="Remove position"
                >
                  <FaTimes />
                </button>
              )}
            </div>
          ))}
        </div>
        {positions.length < 10 && (
          <button
            type="button"
            className="add-position-btn"
            onClick={addPosition}
          >
            <FaPlus /> Add Position
          </button>
        )}
      </div>

      {/* Required Skills */}
      <div className="form-group">
        <label htmlFor="required-skills">Required Skills</label>
        <input
          id="required-skills"
          type="text"
          value={requiredSkills}
          onChange={(e) => setRequiredSkills(e.target.value)}
          placeholder="Python, Machine Learning, Statistics (comma-separated)"
        />
        <small>Enter skills separated by commas</small>
      </div>

      {/* Duration and Type - Side by Side */}
      <div className="form-group-row">
        <div className="form-group">
          <label htmlFor="duration">Duration *</label>
          <select
            id="duration"
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            required
          >
            <option value="short-term">Short-term</option>
            <option value="long-term">Long-term</option>
            <option value="ongoing">Ongoing</option>
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="collab-type">Collaboration Type *</label>
          <select
            id="collab-type"
            value={collaborationType}
            onChange={(e) => setCollaborationType(e.target.value)}
            required
          >
            <option value="research">Research</option>
            <option value="paper-writing">Paper Writing</option>
            <option value="data-analysis">Data Analysis</option>
            <option value="experiment">Experiment</option>
            <option value="review">Review</option>
            <option value="other">Other</option>
          </select>
        </div>
      </div>

      {/* Research Room Link */}
      <div className="form-group">
        <label htmlFor="research-room">Link to Research Room (Optional)</label>
        <select
          id="research-room"
          value={researchRoom}
          onChange={(e) => setResearchRoom(e.target.value)}
          disabled={loadingRooms}
        >
          <option value="">No research room</option>
          {myRooms.map((room) => (
            <option key={room._id} value={room._id}>
              {room.name}
            </option>
          ))}
        </select>
        <small>
          Accepted applicants will be automatically added to the selected room
        </small>
      </div>

      {/* Application Deadline */}
      <div className="form-group">
        <label htmlFor="deadline">Application Deadline (Optional)</label>
        <input
          id="deadline"
          type="date"
          value={deadline}
          onChange={(e) => setDeadline(e.target.value)}
          min={new Date().toISOString().split("T")[0]}
        />
      </div>

      {/* Form Actions */}
      <div className="form-actions">
        <button type="button" onClick={onCancel} className="cancel-btn">
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading || !title.trim() || !description.trim()}
          className="submit-btn"
        >
          {loading ? "Creating..." : "Create Collaboration"}
        </button>
      </div>
    </form>
  );
};

export default React.memo(CollaborationPostForm);
